// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getToken } from 'firebase/app-check';
import { OEM_KEY_TO_PROJECT_MAP } from '@ekhodealer/ekho-common';
import { getDistance } from 'geolib';
import { auth, appCheck, FUNCTION_HOST } from './firebaseConfig';
import { PREQUAL_OUTCOMES } from './common/data/constants';

// Helper function for getting the AppCheck token
async function getAppCheckToken() {
	try {
		const appCheckToken = await getToken(appCheck, false);
		return appCheckToken;
	} catch (error) {
		throw new Error('Failed to get AppCheck token: ', error);
	}
}

// function that returns the correct headers for a request
function getRequestHeaders(appCheckToken, authToken) {
	// Add app check token to request headers
	const functionHeaders = { 'X-Firebase-AppCheck': appCheckToken.token };

	if (authToken) {
		functionHeaders.Authorization = `Bearer ${authToken}`;
	}

	// Generate a 32-character hexadecimal trace ID
	const traceId = uuidv4().replace(/-/g, '');
	// Generate a 16-character hexadecimal span ID
	const spanId = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
		.toString(16)
		.padStart(16, '0');
	// Set the sampled flag to '01' to indicate the trace should be sampled
	const traceFlags = '01';

	// Construct the traceparent header
	const traceparent = `00-${traceId}-${spanId}-${traceFlags}`;

	functionHeaders.traceparent = traceparent;

	return functionHeaders;
}

export async function addPlacedOrderIdToCheckoutCart(placedOrderId, checkoutCartId) {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		placedOrderId,
		checkoutCartId,
	};
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/addPlacedOrderId`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		if (response.status !== 200) {
			throw new Error(`Error adding placed order id to checkout cart. Response: ${response}`);
		}
		return response;
	} catch (e) {
		throw new Error(`Error adding placed order id to checkout cart: ${e}`);
	}
}

export const createPlacedOrderObj = async (placedOrderReqBody) => {
	try {
		// changing to unauthenticated call
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/placedOrder_oncreate_v2`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, placedOrderReqBody, {
			headers: getRequestHeaders(appCheckToken),
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error placing order: ${e}`);
	}
};

// create a function that gets the vehicles from an order using the purchasedVehicle_onfetch/vehiclesFromPlacedOrder/:placedOrderId
export const getVehiclesFromPlacedOrder = async (placedOrderId, buyerId) => {
	try {
		// remove authentication so that this can be called by buyers who are not logged in
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/purchasedVehicle_onfetch_v2/vehiclesFromPlacedOrder/${placedOrderId}`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY, buyerId },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching vehicles from placed order: ${e}`);
	}
};

export const getCurrentAdditionalDues = async (purchasedVehicleId) => {
	try {
		const url = `${FUNCTION_HOST}/purchasedVehicle_onfetch_v2/currentAdditionalDues/${purchasedVehicleId}`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				isForDocuments: false,
			},
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching additional dues: ${e}`);
	}
};

export const createPreorderObjs = async (preorders, paymentIntentId, paymentInvoice) => {
	try {
		const body = {
			oemKey: process.env.REACT_APP_OEM_KEY,
			preorders,
			paymentIntentId,
			paymentInvoice,
		};
		const url = `${FUNCTION_HOST}/placedOrder_oncreate_v2/preorder`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error creating preorders: ${e}`);
	}
};

export const updatePaymentIntentAmountOnly = async (
	paymentIntentId,
	amount,
	processingFeeInc,
	processingFeeIncAmount,
) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		paymentIntentId,
		amount,
		processingFeeInc,
		processingFeeIncAmount,
	};
	try {
		// make this call unauthenticated so that it can be called by buyers who are not logged in
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/stripePayment_v2/updatePaymentIntentAmountOnly`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error updating payment intent amount: ${e}`);
	}
};

export const updatePaymentIntentAmount = async (
	paymentIntentId,
	dbObj,
	dbObjIds,
	amount,
	paymentDataPath,
	processingFeeInc,
	processingFeeIncAmount,
) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		paymentIntentId,
		dbObj,
		dbObjIds,
		amount,
		paymentDataPath,
		processingFeeInc,
		processingFeeIncAmount,
	};
	try {
		// make this call unauthenticated so that it can be called by buyers who are not logged in
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/stripePayment_v2/updatePaymentIntentAmount`;
		const appCheckToken = await getAppCheckToken();
		await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
	} catch (e) {
		throw new Error(`Error updating payment intent amount: ${e}`);
	}
};

export const handlePaymentIntentConfirmed = async (paymentIntentId) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		paymentIntentId,
	};
	try {
		const url = `${FUNCTION_HOST}/stripe_payment_handler_v2/handlePaymentIntentConfirmed`;
		const appCheckToken = await getAppCheckToken();
		await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
	} catch (e) {
		throw new Error(`Error handling payment intent confirmation: ${e}`);
	}
};

export const checkBuyerWithEmailExists = async (email) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		email,
	};
	try {
		const url = `${FUNCTION_HOST}/buyer_onfetch_v2/checkBuyerWithEmailExists`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error checking if buyer object with email exists: ${e}`);
	}
};

export const getUnauthenticatedOemData = async (oemId) => {
	try {
		const url = `${FUNCTION_HOST}/oem_onfetch_v2/${oemId}`;
		// console.log(process.env.REACT_APP_OEM_KEY);
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY, excludeVehicles: true },
		});
		return response.data;
	} catch (e) {
		// console.log('THROWN ERROR WHEN FETCHING OEM: ', e);
		throw new Error(`Error fetching unauthenticated oem data: ${e}`);
	}
};

export const getOemVehicles = async (oemId, vehicleProductIds) => {
	try {
		const url = `${FUNCTION_HOST}/oem_onfetch_v2/vehicles/${oemId}`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				brandKey: process.env.REACT_APP_BRAND_KEY,
				productIds: vehicleProductIds.join(','),
			},
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching oem vehicles: ${e}`);
	}
};

export const getOemPickUpLocations = async (oemId) => {
	try {
		const url = `${FUNCTION_HOST}/oem_onfetch_v2/pickUpLocations/${oemId}`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching oem pick up locations: ${e}`);
	}
};

export async function getServicesProvider(providerId, canonVehicleId) {
	try {
		const url = `${FUNCTION_HOST}/servicesProviders/unauthenticated/${providerId}`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				brandKey: process.env.REACT_APP_BRAND_KEY,
				canonVehicleId,
			},
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching selling services provider: ${e}`);
	}
}

export async function getServicesProviders(canonVehicleId) {
	try {
		const url = `${FUNCTION_HOST}/servicesProviders/unauthenticated`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				brandKey: process.env.REACT_APP_BRAND_KEY,
				onboardingCompleted: true,
				canonVehicleId,
			},
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching services providers: ${e}`);
	}
}

export const getBuyerData = async () => {
	try {
		const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/buyer_onfetch_v2`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken, token),
			params: { oemKey: process.env.REACT_APP_OEM_KEY },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching buyer: ${e}`);
	}
};

export const createCheckoutPaymentIntent = async (
	paymentDescription,
	amountToPay,
	paymentType,
	checkoutCartId,
	oemId,
	processingFeeInc = false,
	processingFeeIncAmount = 0,
	paymentMethodTypes = ['card'],
	hasNoVinnedVehicles = false,
) => {
	// Create Stripe payment intent
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		brandKey: process.env.REACT_APP_BRAND_KEY,
		oemId,
		amountToPay,
		paymentMethodTypes,
		paymentDescription,
		processingFeeInc,
		processingFeeIncAmount,
		paymentType,
		checkoutCartId,
		hasNoVinnedVehicles,
	};
	try {
		const url = `${FUNCTION_HOST}/stripePayment_v2/createCheckoutPaymentIntent`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error creating payment intent associated with a placed order: ${e}`);
	}
};

export const fetchPaymentIntent = async (paymentIntentId) => {
	try {
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/stripePayment_v2/fetchPaymentIntent`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY, paymentIntentId },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching payment intent: ${e}`);
	}
};

//   - logName: string containing log name. Naming convention: separate words with hyphens
//   - logMessage: string containing log message
//   - severity: severity of the log -> enum: INFO, WARNING, ERROR, CRITICAL
//   - labels: object containing custom labels -> userId, oemKey, etc)
// NOTE: always call this function without await. We don't want to block on logging.
export async function createLog(logName, logMessage, severity, labels) {
	const projectId = OEM_KEY_TO_PROJECT_MAP[process.env.REACT_APP_OEM_KEY];
	if (!projectId) {
		throw new Error(`Invalid environment: ${process.env.REACT_APP_OEM_KEY}`);
	}
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		logName,
		logMessage,
		siteName: window.location.hostname.replace('.ekhodealer.com', ''),
		projectId,
		severity,
		labels: { ...labels, oemKey: process.env.REACT_APP_OEM_KEY },
	};
	try {
		const appCheckToken = await getAppCheckToken();
		// if calling from an authenticated environment
		if (auth && auth.currentUser) {
			const token = await auth.currentUser.getIdToken();
			const url = `${FUNCTION_HOST}/log_oncreate_v2`;
			await axios.post(url, body, {
				headers: getRequestHeaders(appCheckToken, token),
			});
		} else {
			const url = `${FUNCTION_HOST}/log_oncreate_v2/unauthenticated`;
			await axios.post(url, body, {
				headers: getRequestHeaders(appCheckToken),
			});
		}
	} catch (e) {
		// Do not block if fail to create logs
		throw new Error(`Error creating log: ${e}`);
	}
}

export async function saveReEngagementLinkClick(reid) {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		reid,
	};
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/saveReEngagementLinkClick`;
		const appCheckToken = await getAppCheckToken();
		await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
	} catch (e) {
		throw new Error(`Error saving re-engagement link click: ${e}`);
	}
}

export const checkoutCartWasOpened = async (checkoutCartId) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		checkoutCartId,
	};
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/checkoutCartWasOpened`;
		const appCheckToken = await getAppCheckToken();
		await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
	} catch (e) {
		throw new Error(`Error marking checkout cart as opened: ${e}`);
	}
};

export const getCheckoutCart = async (checkoutCartId) => {
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_onfetch_v2`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY, checkoutCartId },
		});
		const {
			id,
			cancelUrl,
			nativeDefaultCheckoutUrl,
			ecommUserId,
			ecommOrderId,
			oemKey,
			registrationState,
			vehicles,
			accessories,
			dateCreated,
			orderCreationType,
			isDisabled = false,
			linkUseType = 'MULTI_USE',
			customLinkConfig = {},
			discounts = [],
			looseAccessories,
			isConfigurable = false,
			preorderProdStart,
			preorderCollectShippingAddress,
			metadata = {},
		} = response.data;
		// initialize a uniqueAccessoriesMap and initAccessoryCounts map
		const uniqueAccessoriesMap = new Map();
		const looseAccessoriesMap = new Map();
		const initAccessoryCounts = new Map();
		// loop through accessories and add to uniqueAccessoriesMap and initAccessoryCounts map

		const freqMap = {};

		// if canon
		if (vehicles[0]?.accessories) {
			vehicles.forEach((vehicle) => {
				const { accessories: vehicleAccessories } = vehicle;
				vehicleAccessories.forEach((acc) => {
					const accId = acc.productId;
					acc.id = accId;
					uniqueAccessoriesMap.set(accId, acc);
					// Increment count if exists, otherwise initialize to 1
					initAccessoryCounts.set(accId, (initAccessoryCounts.get(accId) || 0) + 1);
				});
				if (looseAccessories) {
					looseAccessories.forEach((acc) => {
						const accId = acc.productId;
						acc.id = accId;

						if (!freqMap[accId]) freqMap[accId] = [];
						freqMap[accId].push(vehicle.id);

						if (looseAccessoriesMap.has(accId)) {
							const existingAcc = looseAccessoriesMap.get(accId);
							existingAcc.count = (existingAcc.count || 0) + 1;
							looseAccessoriesMap.set(accId, existingAcc);
						} else {
							acc.count = 1;
							looseAccessoriesMap.set(accId, acc);
						}
					});
				}
			});

			const getUniformCount = (arr) => {
				const countMap = arr.reduce((acc, vehicleId) => {
					acc[vehicleId] = (acc[vehicleId] || 0) + 1;
					return acc;
				}, {});

				const counts = Object.values(countMap);
				const allEqual = counts.every((count) => count === counts[0]);
				return allEqual ? counts[0] : null;
			};
			looseAccessoriesMap.forEach((acc) => {
				const uniformCount = getUniformCount(freqMap[acc.id]);
				if (uniformCount) {
					acc.count = uniformCount;
				}
			});

			return {
				id,
				cancelUrl,
				nativeDefaultCheckoutUrl,
				ecommUserId,
				ecommOrderId,
				oemKey,
				registrationState,
				vehicles,
				uniqueAccessoriesMap,
				looseAccessoriesMap,
				initAccessoryCounts,
				dateCreated,
				orderCreationType,
				isDisabled,
				linkUseType,
				customLinkConfig,
				looseAccessories,
				discounts,
				isConfigurable,
				preorderProdStart,
				preorderCollectShippingAddress,
				metadata,
			};
		}

		// else if old
		let hasBoltOns = false;
		// loop through accessories and add to uniqueAccessoriesMap and initAccessoryCounts map
		accessories.forEach((accessory) => {
			uniqueAccessoriesMap.set(accessory.id, accessory);
			initAccessoryCounts.set(accessory.id, accessory.count);
			// if accessory.boltOnVehicleIds exists, set hasBoltOns to true
			if (accessory.boltOnVehicleIds) {
				hasBoltOns = true;
			}
		});
		return {
			id,
			cancelUrl,
			nativeDefaultCheckoutUrl,
			ecommUserId,
			ecommOrderId,
			oemKey,
			registrationState,
			vehicles,
			uniqueAccessoriesMap,
			initAccessoryCounts,
			dateCreated,
			orderCreationType,
			hasBoltOns,
			isDisabled,
			linkUseType,
			customLinkConfig,
			discounts,
			isConfigurable,
			preorderProdStart,
			preorderCollectShippingAddress,
			metadata,
		};
	} catch (e) {
		throw new Error(`Error fetching checkout cart: ${e}`);
	}
};

export const fetchGoogleMapsApiKey = async () => {
	try {
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/secrets_onfetch_v2/fetchGoogleMapsApiKey`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			// headers: getRequestHeaders(appCheckToken, token),
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching google maps API key: ${e}`);
	}
};

export const getPreQualDecision = async (buyerData, regState, vehicleProductId) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		brandKey: process.env.REACT_APP_BRAND_KEY,
		buyerData,
		regState,
		vehicleProductId,
	};
	try {
		const url = `${FUNCTION_HOST}/prequal_oncreate_v2/`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		return response.data;
	} catch (e) {
		return {
			decision: PREQUAL_OUTCOMES.DENIED,
			rejectionReasons: ['Error getting prequal data'],
		};
	}
};

export const getStateTaxAndTARData = async () => {
	try {
		const url = `${FUNCTION_HOST}/state_onfetch_v2/allStates/taxAndTARSupportData`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching state taxes and fees: ${e}`);
	}
};

export const createAbandonedCart = async (
	checkoutCartId,
	buyerEmail,
	setAbandonedCartId,
	setCreatingAbandonedCart,
) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		checkoutCartId,
		buyerEmail,
	};
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/createAbandonedCart`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		if (response.status !== 200) {
			throw new Error(`Error creating abandoned cart. Response: ${response}`);
		}
		// only return the abandonedCartId if it exists
		if (response.data.abandonedCartId) {
			const newAbandonedCartId = response.data.abandonedCartId;
			setAbandonedCartId(newAbandonedCartId);
			setCreatingAbandonedCart(false);
			return response.data.abandonedCartId;
		}
		throw new Error('Error creating abandoned cart. No abandonedCartId returned');
	} catch (e) {
		throw new Error(`Error creating abandoned cart: ${e}`);
	}
};

export const updateAbandonedCartServerCall = async (abandonedCartId, updateData) => {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		abandonedCartId,
		updateData,
	};
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/updateAbandonedCart`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		if (response.status !== 200) {
			throw new Error(`Error updating abandoned cart. Response: ${response}`);
		}
		return response;
	} catch (e) {
		throw new Error(`Error updating abandoned cart: ${e}`);
	}
};

export const saveCreditApplication = async (
	vehiclePurchasedId,
	creditApplication,
	submitApp = true,
	session = null,
	brandKey = process.env.REACT_APP_BRAND_KEY,
) => {
	try {
		// const token = await auth.currentUser.getIdToken();
		const url = `${FUNCTION_HOST}/purchasedVehicle_oncreate_v2/${vehiclePurchasedId}/creditApplication`;

		const body = {
			creditApplication,
			oemKey: process.env.REACT_APP_OEM_KEY,
			submitApp,
			session,
			brandKey,
		};
		const appCheckToken = await getAppCheckToken();
		await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
	} catch (e) {
		let errMessage = 'Unknown error';
		if (axios.isAxiosError(e)) {
			errMessage = JSON.stringify({
				message: e.message,
				status: e.response?.status,
				statusText: e.response?.statusText,
				url: e.config?.url,
				method: e.config?.method,
				data: e.response?.data,
			});
		} else if (e instanceof Error) {
			errMessage = e.message;
		}

		throw new Error(`Error saving credit application: ${errMessage}`);
	}
};

export async function updateFinancingActionItem(
	purchasedVehicleId,
	updatedActionItem,
	daysUntilSubmission = null,
) {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		updatedActionItem,
		daysUntilSubmission,
	};
	try {
		const url = `${FUNCTION_HOST}/purchasedVehicle_oncreate_v2/${purchasedVehicleId}/financingActionItem`;
		const appCheckToken = await getAppCheckToken();
		await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
	} catch (e) {
		throw new Error(`Error updated financing action item: ${e}`);
	}
}

function geocodeAddressClientSide(address) {
	return new Promise((resolve, reject) => {
		const geocoder = new window.google.maps.Geocoder();
		geocoder.geocode({ address }, (results, status) => {
			if (status === 'OK') {
				const latitude = results[0].geometry.location.lat();
				const longitude = results[0].geometry.location.lng();
				resolve({ latitude, longitude });
			} else {
				reject(new Error(`Geocoding failed: ${status}`));
			}
		});
	});
}

async function getDistanceMatrixWithClientLibrary(origin, destination) {
	return new Promise((resolve, reject) => {
		const service = new window.google.maps.DistanceMatrixService();
		service.getDistanceMatrix(
			{
				origins: [origin],
				destinations: [destination],
				travelMode: 'DRIVING',
				unitSystem: window.google.maps.UnitSystem.IMPERIAL,
			},
			async (response, status) => {
				// Odd double status format from the Google Maps API
				if (status === 'OK') {
					const result = response.rows[0].elements[0];
					if (result.status === 'OK') {
						const distanceInMiles = parseFloat(
							(result.distance.value / 1609.34).toFixed(2),
						);
						const simplifiedResponse = {
							distance: distanceInMiles,
							duration: result.duration.text,
						};
						resolve(simplifiedResponse);
					} else if (result.status === 'ZERO_RESULTS') {
						try {
							// Fallback to geocoding and Haversine distance calculation
							const originCoords = await geocodeAddressClientSide(origin);
							const destinationCoords = await geocodeAddressClientSide(destination);
							const distanceInMeters = getDistance(originCoords, destinationCoords);
							const distanceInMiles = parseFloat(
								(distanceInMeters / 1609.34).toFixed(2),
							);
							const simplifiedResponse = {
								distance: distanceInMiles,
								duration: 'N/A', // Duration is not available with Haversine calculation
							};
							resolve(simplifiedResponse);
						} catch (error) {
							reject(new Error(`Geocoding fallback failed: ${error.message}`));
						}
					} else {
						reject(new Error(`Distance Matrix request failed: ${status}`));
					}
				} else {
					reject(new Error(`Distance Matrix request failed: ${status}`));
				}
			},
		);
	});
}

export async function getDistanceBetweenAddressesAsync(originAddress, destinationAddress) {
	try {
		const response = await getDistanceMatrixWithClientLibrary(
			originAddress,
			destinationAddress,
		);
		return response;
	} catch (error) {
		throw new Error(`Error getting distance between addresses: ${error}`);
	}
}

export async function sendTransactionalEmail(messageData, messageId) {
	const body = {
		oemKey: process.env.REACT_APP_OEM_KEY,
		messageData,
		messageId,
	};
	try {
		const url = `${FUNCTION_HOST}/send_transactional_email_v2/sendTransactionalEmail`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});
		if (response.status !== 200) {
			throw new Error(`Error sending transactional email. Response: ${response}`);
		}
		return response;
	} catch (e) {
		throw new Error(`Error sending transactional email: ${e}`);
	}
}

export const getVehicleConfig = async (productId) => {
	try {
		const url = `${FUNCTION_HOST}/vehicleConfig_onfetch_v2`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				brandKey: process.env.REACT_APP_BRAND_KEY,
				productId,
			},
		});
		const { vehicleConfig, defaultVehicleConfig } = response.data;

		return {
			vehicleConfigData: vehicleConfig,
			defaultVehicleConfigData: defaultVehicleConfig,
		};
	} catch (e) {
		throw new Error(`Error fetching vehicle config: ${e}`);
	}
};

export const createCheckoutCart = async (body) => {
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY },
		});
		const { ekhoCheckoutCartId } = response.data;

		return { cartId: ekhoCheckoutCartId };
	} catch (e) {
		throw new Error(`Error fetching vehicle config: ${e}`);
	}
};

export const sendLeadConfirmationEmailToBuyer = async (payload) => {
	try {
		const url = `${FUNCTION_HOST}/checkoutCart_oncreate_v2/sendConfirmationEmail`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, payload, {
			headers: getRequestHeaders(appCheckToken),
			params: { oemKey: process.env.REACT_APP_OEM_KEY },
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error sending lead confirmation email: ${e}`);
	}
};

export const fetchRelevantFinancingPrograms = async (vehicle, oemId, regState = null) => {
	try {
		const appCheckToken = await getAppCheckToken();
		const url = `${FUNCTION_HOST}/lender_onfetch_v2/relevantFinancingPrograms`;
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				regState,
				oemId,
				vehicleProductId: vehicle.productId,
			},
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching lenders with valid programs: ${e}`);
	}
};

// not being used since implementation is not yet done
export const getPickupProviders = async () => {
	try {
		const url = `${FUNCTION_HOST}/servicesProvider_onfetch_v2/providers/pickup`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
				brandKey: process.env.REACT_APP_BRAND_KEY,
			},
		});
		const { data } = response;
		return data;
	} catch (e) {
		throw new Error(`Error getting closest pickup: ${e}`);
	}
};

export const calculateTaxEstimates = async (
	checkoutCartId,
	lineItems,
	address,
	shipFromAddress,
	forceRefresh = false,
) => {
	try {
		const body = {
			oemKey: process.env.REACT_APP_OEM_KEY,
			lineItems,
			address: { ...address },
			shipFromAddress,
			referenceType: 'CheckoutCart',
			referenceId: checkoutCartId,
			forceRefresh,
		};

		const url = `${FUNCTION_HOST}/taxes_oncreate_v2`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.post(url, body, {
			headers: getRequestHeaders(appCheckToken),
		});

		if (response.status !== 200) {
			throw new Error(`Error calculating tax estimates. Response: ${response}`);
		}
		return response.data;
	} catch (e) {
		console.error('Error calculating tax estimates:', e);
		throw new Error(`Error calculating tax estimates: ${e}`);
	}
};

export const createSession = async () => {
	const url = `${FUNCTION_HOST}/sessions/`;
	const appCheckToken = await getAppCheckToken();
	try {
		const response = await axios.post(
			url,
			{
				oemKey: process.env.REACT_APP_OEM_KEY,
				appOriginId: 'checkout',
			},
			{
				headers: getRequestHeaders(appCheckToken),
			},
		);
		return response.data;
	} catch (error) {
		throw new Error(`Error creating session: ${error}`);
	}
};

// Inventory APIs
// Get available inventory at a given inventory service provider
export const getAvailableInventory = async (filters) => {
	try {
		const url = `${FUNCTION_HOST}/inventory/available?ownerId=${filters.ownerId}&productId=${filters.productId}&color=${filters.color}`;
		const appCheckToken = await getAppCheckToken();
		const response = await axios.get(url, {
			headers: getRequestHeaders(appCheckToken),
			params: {
				oemKey: process.env.REACT_APP_OEM_KEY,
			},
		});
		return response.data;
	} catch (e) {
		throw new Error(`Error fetching available inventory: ${e}`);
	}
};
